var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    AccelerateSupport           = require("enyo-ui/AccelerateSupport"),
    Control                     = require("enyo/Control"),
    PerformanceMode             = require("enyo-perf/PerformanceMode"),
    PerformanceModeQueueSupport = require("enyo-perf/PerformanceModeQueueSupport"),
    Slider,
    kind                        = require("enyo/kind"),
    throttle                    = require("enyo-perf/utils").throttle,
    utils                       = require("enyo/utils");

require("enyo-ui");

/**
 * [Slider]{@link enyo-ui.Slider} provides the infrastructure to create, render
 * and navigate through a collection of slide controls for each record in a
 * dataset, one slide at a time.
 *
 * It needs an adapter for the particular type of data it will receive - such as
 * [SliderArraySupport]{@link enyo-ui.SliderArraySupport}, for array data.
 *
 * @ui
 * @class   enyo-ui.Slider
 * @extends enyo.Control
 * @mixes   enyo-perf.AbsoluteShowingQueueSupport
 * @mixes   enyo-perf.PerformanceModeQueueSupport
 * @mixes   enyo-ui.AccelerateSupport
 * @public
 */
Slider = module.exports = kind(/** @lends enyo-ui.Slider.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.Slider",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    mixins: [
        AbsoluteShowingQueueSupport,
        PerformanceModeQueueSupport,
        AccelerateSupport
    ],

    /**
     * @private
     */
    classes: "enyo-ui-slider",

    /**
     * Whether to gpu-accelerate the slides' DOM nodes.
     *
     * @type     {boolean}
     * @default  true
     * @public
     */
    accelerate: true,

    /**
     * The kind responsible for arranging slides within the
     * slideContainer.
     *
     * @type {enyo-ui.SliderArranger}
     * @public
     */
    arrangerKind: null,

    /**
     * Options for the arranger
     *
     * @type {Object=}
     * @public
     */
    arrangerOptions: null,

    /**
     * The kind that will be rendered for each record in the dataset.
     *
     * @type {enyo.Control}
     * @public
     */
    slideKind: null,

    /**
     * Optional extra options to merge into the slide kind at creation time.
     *
     * @type {Object=}
     * @public
     */
    slideOptions: null,

    /**
     * The amount of slides that will initially be preloaded.
     *
     * @type    {number}
     * @default 10
     * @public
     */
    bufferSize: 10,

    /**
     * Whether the buffering process should be asynchronous, adding one
     * slide at at time.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    bufferAsync: false,

    /**
     * Sets the orientation of the slider. The slider will respond to drag
     * gestures along this axis and the arranger may make use of this value
     * as well for animated transitions.
     *
     * Supported values are "vertical" and "horizontal".
     *
     * @type    {string}
     * @default "horizontal"
     * @public
     */
    axis: "horizontal",

    /**
     * The debounce duration (in ms) between consecutive transitions. If
     * multiple transitions get requested within this time frame, only the
     * first and last one will get executed.
     *
     * @type    {number}
     * @default 50
     * @public
     */
    transitionTimeout: 50,

    /**
     * The dataset feeding this slider.
     *
     * @type {*}
     * @public
     */
    data: null,

    /**
     * The length of the dataset.
     *
     * @type {number}
     * @readonly
     * @public
     */
    length: 0,

    /**
     * This hash maps the unique ids of records in the dataset to slide
     * controls. This mapping will be updated any time a slide is created or
     * destroyed.
     *
     * @type {Object}
     * @private
     */
    recordUidToSlide: null,

    /**
     * An array of indexes for which we need to add slides.
     *
     * @type {Array}
     * @private
     */
    unbufferedIndexes: null,

    /**
     * A flag that tells us if the slider is currently buffering slides.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    buffering: false,

    /**
     * The currently active slide.
     *
     * @type {?number}
     * @readonly
     * @public
     */
    activeSlide: null,

    /**
     * The slide that's currently transitioning into active state.
     *
     * @type {enyo.Control}
     * @readonly
     * @public
     */
    activatingSlide: null,

    /**
     * Unthrottled reference to index from the last requested transition.
     *
     * @type {?number}
     * @private
     */
    requestedIndex: null,

    /**
     * Sets the direction in which the slider is currently navigating. Supported
     * values are "forward" and "backward".
     *
     * @type    {?string}
     * @default "forward"
     * @public
     */
    direction: "forward",

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.recordUidToSlide  = {};
            this.unbufferedIndexes = [];
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.bufferNext         = this.bindSafely(this.bufferNext);
            this._transitionToIndex = throttle(this.bindSafely(this._transitionToIndex), this.transitionTimeout);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createArranger();
            sup.apply(this, arguments);
        };
    }),

    /**
     * Creates an arranger instance responsible for arranging the slides. All
     * repeater slides will be arranged relative to the bounds of the
     * slideContainer by this arranger.
     *
     * @private
     */
    createArranger: function() {
        this.arranger = kind.createFromKind(this.arrangerKind, this);
    },

    // Destruction
    // --------------------------------

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            var arranger = this.arranger;

            if (arranger && arranger.destroy) {
                arranger.destroy();
            }
            this._transitionToIndex.cancel();
            sup.apply(this, arguments);
        };
    }),

    // Dataset
    // --------------------------------

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {number}  index  The index
     * @return {*}       The record, or `null` if not found.
     * @abstract
     * @protected
     */
    getRecordByIndex: function(index) {
        return this.error("Slider must implement a `getRecordByIndex` method.", index);
    },

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {*}        record  The record
     * @return {?number}  The index, or `null` if not found.
     * @protected
     */
    getIndexByRecord: function(record) {
        return this.error("Slider must implement a `getIndexByRecord` method.", record);
    },

    /**
     * Retrieves a unique identifier from the record.
     *
     * @param  {*}       record  The record
     * @return {string}  The record uid.
     * @abstract
     * @protected
     */
    getRecordUid: function(record) {
        return this.error("Slider must implement a `getRecordUid` method.", record);
    },

    // Slides
    // --------------------------------

    /**
     * Gets the control that will own the slides.
     *
     * @return {Object}  The slide container.
     * @protected
     */
    getSlideContainer: function() {
        return this;
    },

    /**
     * Creates a slide control for the provided record.
     *
     * @param  {*}             record  The record
     * @return {enyo.Control}  The created slide
     * @private
     */
    addSlide: function(record) {
        var slideContainer   = this.getSlideContainer(),
            recordUid        = this.getRecordUid(record),
            recordUidToSlide = this.recordUidToSlide,
            slideKind        = this.getSlideKind(record),
            slideOptions     = this.getSlideOptions(slideKind, record),
            slide;

        // Slide already exists
        if (recordUidToSlide[recordUid]) {
            this.error("Slide for record: " + recordUid + " already exists.");

            return recordUidToSlide[recordUid];
        }

        // Create slide configured not to render immediately
        slide                  = slideContainer.createComponent({
            name:    "slider_slide_" + recordUid,
            kind:    slideKind,
            classes: "enyo-ui-slider__slide " + (slideOptions.classes || ""),
            data:    record,
            slider:  this
        }, slideOptions);
        slide.sliderTransition = this.arranger.createTransition(slide);

        // Ensure the transition gets destroyed when the slide gets destroyed
        slide.extend({
            destroy: kind.inherit(function(sup) {
                return function() {
                    this.sliderTransition.destroy();
                    sup.apply(this, arguments);
                };
            })
        });

        // Index slide
        recordUidToSlide[recordUid] = slide;

        // Arrange the slide
        this.arranger.arrangeSlide(slide);

        // Render slide as needed.
        if (this.hasNode() && !slide.hasNode()) {
            slide.render();
        }

        return slide;
    },

    /**
     * Override this method if the returned kind needs to change based on the
     * record it'll show.
     *
     * @param  {Object=}   record The record
     * @return {Function}  The kind constructor
     * @protected
     */
    getSlideKind: function() {
        return this.slideKind;
    },

    /**
     * Override this method if the returned options need to change based on the
     * kind that will be used.
     *
     * @param  {Function}  kind The slideKind
     * @param  {Object=}   record The record
     * @return {?Object}   The options
     * @protected
     */
    getSlideOptions: function() {
        return this.slideOptions || {};
    },

    /**
     * Gets the slide associated with the record at the provided index in the
     * dataset.
     *
     * @param  {number}         index  The index
     * @return {?enyo.Control}  The slide if found, `null` otherwise.
     * @public
     */
    getSlideForIndex: function(index) {
        var record,
            recordUid;

        if (!this.length) {
            return null;
        }

        index  = this.wrapIndex(index);
        record = this.getRecordByIndex(index);

        if (!record) {
            return null;
        }

        recordUid = this.getRecordUid(record);

        if (!recordUid) {
            return null;
        }

        return this.recordUidToSlide[recordUid] || null;
    },

    /**
     * Gets the slide associated with the record at the provided index in the
     * dataset, a slide will be created on the fly if needed.
     *
     * @param  {number}         index  The index
     * @return {?enyo.Control}  The slide if created, `null` otherwise.
     * @private
     */
    ensureSlideForIndex: function(index) {
        var slide,
            record;

        if (!this.length) {
            return null;
        }

        index = this.wrapIndex(index);
        slide = this.getSlideForIndex(index);

        if (slide) {
            return slide;
        }

        record = this.getRecordByIndex(index);

        if (!record) {
            return null;
        }

        return this.addSlide(record);
    },

    /**
     * Makes sure the slide is removed, no longer mapped to it's record and
     * destroyed.
     *
     * If the destroyed slide was active or in the process of being activated, a
     * fallback index will be activated automatically.
     *
     * @param {Object}  record  The record
     * @private
     */
    removeSlide: function(record) {
        var recordUid = this.getRecordUid(record),
            slide     = this.recordUidToSlide[recordUid];

        if (slide) {
            delete this.recordUidToSlide[recordUid];
            slide.destroy();

            if (slide === this.activeSlide) {
                this.set("activeSlide", null);
                this.transitionToFallback(slide);
            }

            if (slide === this.activatingSlide) {
                this.activatingSlide = null;
                this.transitionToFallback(slide);
            }
        }
    },

    /**
     * Remove slides that do not fall within the range of buffer indexes.
     *
     * @private
     */
    pruneSlides: function() {
        var buffer           = this.getBufferIndexes(),
            index,
            record,
            uid,
            whitelist        = {},
            recordUidToSlide = this.recordUidToSlide,
            recordUid,
            slide;

        // Build a whitelist of slides that should not be removed
        while (buffer.length) {
            index  = buffer.pop();
            record = this.getRecordByIndex(index);

            if (record) {
                uid = this.getRecordUid(record);
            }

            if (uid !== null && uid !== undefined) {
                whitelist[uid] = true;
            }
        }

        // Remove all slides that are not on the whitelist, not active, not
        // activating and not animating
        for (recordUid in recordUidToSlide) {
            if (recordUidToSlide.hasOwnProperty(recordUid)) {
                slide = recordUidToSlide[recordUid];

                if (!whitelist[recordUid]
                    && slide !== this.activeSlide
                    && slide !== this.activatingSlide
                    && !slide.sliderTransition.animating
                ) {
                    this.removeSlide(slide.data);
                }
            }
        }
    },

    /**
     * Removes all slides
     *
     * @public
     */
    clearSlides: function() {
        var recordUidToSlide = this.recordUidToSlide,
            recordUid;

        for (recordUid in recordUidToSlide) {
            if (recordUidToSlide.hasOwnProperty(recordUid)) {
                recordUidToSlide[recordUid].destroy();
                delete recordUidToSlide[recordUid];
            }
        }

        this.set("activeSlide", null);
        this.activatingSlide = null;
        this.requestedIndex  = null;
    },

    // Render, Resize, Reflow
    // --------------------------------

    /**
     * Prune slides to reduce scope of the reflow, then let the arranger reflow
     * the slider and arrange it's remaining slides.
     *
     * TODO: Make sure drag interaction is stopped (once implemented)
     *
     * @private
     */
    reflow: function() {
        this.pruneSlides();
        this.arranger.reflow();
    },

    // Slide Buffering
    // --------------------------------

    /**
     * Creates a list of indexes for which slides should be preloaded and adds
     * them as needed.
     *
     * @public
     */
    updateBuffer: function() {
        var unbufferedIndexes;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("updateBuffer");

            return;
        }
        this.removeFromAbsoluteShowingQueue("updateBuffer");

        // Defer when in performanceMode
        if (PerformanceMode.on) {
            this.addToPerformanceModeQueue("updateBuffer");

            return;
        }
        this.removeFromPerformanceModeQueue("updateBuffer");

        if (!this.buffering) {
            unbufferedIndexes = this.getBufferIndexes();

            if (unbufferedIndexes.length) {
                this.set("buffering", true);
                this.unbufferedIndexes = unbufferedIndexes;
                this.bufferNext();
            }
        }
    },

    /**
     * Calculates a sorted list of indexes for which to add a slide. The length
     * of the list will be limited to the
     * [bufferSize]{@link enyo-ui.Slider#bufferSize}.
     *
     * @return {Array}  The indexes.
     * @private
     */
    getBufferIndexes: function() {
        var length      = this.length,
            bufferSize  = this.bufferSize,
            minIndex    = 0,
            maxIndex    = length - 1,
            direction   = this.direction,
            activeIndex = this.getActiveIndex(),
            lowestIndex,
            highestIndex,
            indexes     = [];

        if (length < 1 || bufferSize < 1 || activeIndex === null) {
            return indexes;
        }

        lowestIndex  = activeIndex;
        highestIndex = lowestIndex;

        indexes.push(lowestIndex);

        while (indexes.length < bufferSize && (
            lowestIndex > minIndex || highestIndex < maxIndex
        )) {
            if (direction === "forward") {
                if (highestIndex < maxIndex) {
                    ++highestIndex;
                    indexes.push(highestIndex);
                }

                if (lowestIndex > minIndex) {
                    direction = "backward";
                }
            }
            else {
                if (lowestIndex > minIndex) {
                    --lowestIndex;
                    indexes.push(lowestIndex);
                }

                if (highestIndex < maxIndex) {
                    direction = "forward";
                }
            }
        }

        return indexes.reverse();
    },

    /**
     * Evaluates if there are any unbuffered indexes. If so, an index is popped
     * off the stack and a slide added as needed.
     *
     * This method will be deferred if the slider is not absolutely showing or
     * if the application is in performance mode.
     *
     * @protected
     */
    bufferNext: function() {
        var bufferAsync       = this.bufferAsync,
            unbufferedIndexes = this.unbufferedIndexes;

        // Defer if not showing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("bufferNext");

            return;
        }
        this.removeFromAbsoluteShowingQueue("bufferNext");

        // Defer when in performanceMode
        if (PerformanceMode.on && this.shouldDeferBufferNextInPerformanceMode()) {
            this.addToPerformanceModeQueue("bufferNext");

            return;
        }
        this.removeFromPerformanceModeQueue("bufferNext");

        if (this.bufferTimeout) {
            this.bufferTimeout = null;
        }

        // Schedule next slide if needed, stop otherwise.
        if (unbufferedIndexes.length === 0) {
            this.stopBuffering();

            return;
        }

        // Ensure a slide for the next index
        this.ensureSlideForIndex(unbufferedIndexes.pop());

        // Schedule next index
        bufferAsync ? this.requestAsyncBufferNext() : this.bufferNext();
    },

    /**
     * Always defer bufferNext in PerformanceMode. Override if another strategy
     * is desired.
     *
     * @returns {bool} Whether to defer buffering the next slide.
     * @protected
     */
    shouldDeferBufferNextInPerformanceMode: function() {
        return true;
    },

    /**
     * Schedules bufferNext call to be triggered at the end of the stack.
     *
     * @private
     */
    requestAsyncBufferNext: function() {
        if (!this.bufferTimeout) {
            this.bufferTimeout = setTimeout(this.bufferNext, 0);
        }
    },

    /**
     * Cancels the scheduled bufferNext call.
     *
     * @protected
     */
    cancelAsyncBufferNext: function() {
        this.bufferTimeout && clearTimeout(this.bufferTimeout);
        this.bufferTimeout = null;
    },

    /**
     * Stops any currently active buffering loop.
     *
     * @public
     */
    stopBuffering: function() {
        if (this.buffering) {
            this.removeFromAbsoluteShowingQueue("updateBuffer");
            this.removeFromPerformanceModeQueue("updateBuffer");
            this.removeFromAbsoluteShowingQueue("bufferNext");
            this.removeFromPerformanceModeQueue("bufferNext");
            this.cancelAsyncBufferNext();
            this.set("buffering", false);
        }
    },

    /**
     * Continues any previously interrupted buffering loop.
     *
     * @public
     */
    continueBuffering: function() {
        if (this.unbufferedIndexes.length > 0) {
            this.set("buffering", true);
            this.bufferAsync ? this.requestAsyncBufferNext() : this.bufferNext();
        }
    },

    // Slide activity
    // --------------------------------

    /**
     * Makes sure the activated slide is no longer marked as activating.
     *
     * @private
     */
    activeSlideChanged: function() {
        var activeSlide = this.activeSlide;

        // Ensure newly activated slide is no longer marked as activating
        if (activeSlide === this.activatingSlide) {
            this.activatingSlide = null;
        }

        // Ensure newly activated slide is no longer scheduled for activation
        if (this.requestedIndex !== null
            && activeSlide === this.getSlideForIndex(this.requestedIndex)) {
            this.requestedIndex = null;
        }

        // Update buffered set of slides
        activeSlide !== null && this.updateBuffer();
    },

    /**
     * Returns the index of of the current active slide. If no slide is active,
     * a value of 'null' is returned.
     *
     * @return {?number} The index of the active slide, or `null` if none exists
     * @public
     */
    getActiveIndex: function() {
        var activeSlide = this.activeSlide;

        return activeSlide !== null ? this.getIndexByRecord(activeSlide.data) : null;
    },

    /**
     * Returns the index of of the currently activating slide. If no slide is
     * activating, a value of 'null' is returned.
     *
     * @return {?number} The index of the activating slide, or `null` if none
     *                  exists
     * @public
     */
    getActivatingIndex: function() {
        var activatingSlide = this.activatingSlide;

        return activatingSlide !== null ? this.getIndexByRecord(activatingSlide.data) : null;
    },

    /**
     * Wraps the index to remain within the length of the dataset.
     *
     * @param  {number}  index  The index to wrap
     * @return {number}  The wrapped index
     * @public
     */
    wrapIndex: function(index) {
        var range = this.length;

        return ((index % range) + range) % range;
    },

    // Snap
    // --------------------------------

    /**
     * Snaps to a particular index.
     *
     * @param  {number}  index  The index to activate
     * @public
     */
    snapToIndex: function(index) {
        this.requestedIndex = index;
        this.arranger.snapTo(this.ensureSlideForIndex(index));
    },

    /**
     * Snaps to a particular slide.
     *
     * @param  {enyo.Control}  slide  The slide to activate
     * @public
     */
    snapToSlide: function(slide) {
        this.requestedIndex = this.getIndexByRecord(slide.data);
        this.arranger.snapTo(slide);
    },

    /**
     * Snaps to the next slide.
     *
     * @public
     */
    snapToNext: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index === null && this.length) {
            index = -1;
        }

        if (index !== null) {
            ++index;
        }

        this.requestedIndex = index;
        index !== null      && this.snapToIndex(index);
    },

    /**
     * Snaps to the previous slide.
     *
     * @public
     */
    snapToPrevious: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index === null && this.length) {
            index = 1;
        }

        if (index !== null) {
            --index;
        }

        this.requestedIndex = index;
        index !== null      && this.snapToIndex(index);
    },

    // Transition
    // --------------------------------

    /**
     * Transitions to a particular index.
     *
     * Transitions are throttled to prevent too many animating slides at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link enyo-ui.Slider#requestedIndex}.
     *
     * @param  {number}  index  The index to activate
     * @public
     */
    transitionToIndex: function(index) {
        this.requestedIndex = index;
        this._transitionToIndex();
    },

    /**
     * Transitions to a particular slide.
     *
     * Transitions are throttled to prevent too many animating slides at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link enyo-ui.Slider#requestedIndex}.
     *
     * @param  {enyo.Control}  slide  The slide to activate
     * @public
     */
    transitionToSlide: function(slide) {
        this.requestedIndex = this.getIndexByRecord(slide.data);
        this._transitionToIndex();
    },

    /**
     * Transitions to the next slide.
     *
     * Transitions are throttled to prevent too many animating slides at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link enyo-ui.Slider#requestedIndex}.
     *
     * @public
     */
    transitionToNext: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index === null && this.length) {
            index = -1;
        }

        if (index !== null) {
            ++index;
        }

        this.requestedIndex = index;
        index !== null      && this._transitionToIndex();
    },

    /**
     * Transitions to the previous slide.
     *
     * Transitions are throttled to prevent too many animating slides at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link enyo-ui.Slider#requestedIndex}.
     *
     * @public
     */
    transitionToPrevious: function() {
        var index = this.requestedIndex;

        if (index === null) {
            index = this.getActiveIndex();
        }

        if (index === null && this.length) {
            index = 1;
        }

        if (index !== null) {
            --index;
        }

        this.requestedIndex = index;
        index !== null      && this._transitionToIndex();
    },

    /**
     * Searches for slides at lower indexes until it finds one. If none is found
     * it'll look at higher indexes until it finds one.
     *
     * If a fallback slide is encountered the slider will transition to it,
     * otherwise no further action is taken.
     *
     * Transitions are throttled to prevent too many animating slides at once.
     * Once a transition is applied it'll apply the [last requested
     * index]{@link enyo-ui.Slider#requestedIndex}.
     *
     * @param  {enyo.Control}  slide  The slide for which to find a fallback
     * @private
     */
    transitionToFallback: function(slide) {
        var index    = this.getIndexByRecord(slide.data),
            minIndex = index,
            maxIndex = index,
            fallback;

        while (minIndex > 0 && !fallback) {
            --minIndex;
            fallback = this.getSlideForIndex(minIndex);
        }

        while (maxIndex < this.length - 1 && !fallback) {
            ++maxIndex;
            fallback = this.getSlideForIndex(maxIndex);
        }

        fallback && this.transitionToSlide(fallback);
    },

    /**
     * Transitions to the slide for
     * [requestedIndex]{@link enyo-ui.Slider#requestedIndex}.
     *
     * @private
     */
    _transitionToIndex: function() {
        var index           = this.requestedIndex,
            wrappedIndex    = this.wrapIndex(index),
            activeIndex     = this.getActiveIndex(),
            activatingIndex = this.getActivatingIndex(),
            direction,
            slide;

        if (index === null
            || this.length === 0
            || (wrappedIndex === activeIndex
                && activatingIndex === null)
            || wrappedIndex === activatingIndex
        ) {
            return;
        }

        this.direction       = direction = index < activeIndex ? "backward" : "forward";
        slide                = this.ensureSlideForIndex(index);
        this.activatingSlide = slide;
        this.arranger.transitionTo(slide, index, direction);
    }

});

/**
 * This method allows enyo.kind to extend the `arrangerOptions` and
 * `slideOptions` hashes when creating super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
Slider.concat = function(ctor, props) {
    var proto   = ctor.prototype || ctor,
        options = [
            "arrangerOptions",
            "slideOptions"
        ];

    options.forEach(function(prop) {
        var extension = proto[prop] ? utils.clone(proto[prop]) : {};
        proto[prop]   = utils.mixin(extension, props[prop]);
        delete props[prop];
    });
};
